import { Button, Form, Space, Popconfirm, Typography } from "antd";
import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import InterventionsTable from "@/components/ListTable/InterventionsTable";
import { STORAGE_KEYS } from "@/constants/StorageKeys";
import { getDirectParentByType } from "@/entities/ancestor";
import {
  INTERVENTIONS_FILTERS,
  INTERVENTIONS_INCLUDES,
} from "@/entities/interventions";
import { useBatch } from "@/hooks/queries";
import { useAppNotification } from "@/modules/app-notification";
import { validationErrorParser } from "@/modules/form-validation";
import { ListProvider } from "@/modules/list";
import { SidePanelFooter, useSidePanel } from "@/modules/side-panel";
import { Alert } from "@/ui/alert";

const { useForm } = Form;

// 160px = side panel header height + side panel content spacing + side panel footer height
const TABLE_VH_DIFF = 160;

const invalidateKeys = [
  ["projects", "get"],
  ["interventions", "list"],
];

const Associate = ({ project, model }) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const { notify } = useAppNotification();
  const { closeSidePanel } = useSidePanel();

  const location = getDirectParentByType(project.ancestors, "location");

  const [selectedModels, setSelectedModels] = useState([]);
  const associatedInterventions = selectedModels.filter(
    (model) => !!model.project_id
  );
  const mutation = useBatch(model, "update", { invalidateKeys });

  const maxAssociatedDisplay = 3;

  const onUpdate = () => {
    const data = {
      [model]: selectedModels.map((model) => model.id),
      project_id: project.id,
    };
    mutation.mutate(data, {
      onSuccess: () => {
        notify.success({
          title: t("project:associate.success.title"),
          description: t("project:associate.success.description", {
            count: selectedModels.length,
          }),
        });
        setSelectedModels([]);
      },
    });
  };

  function onClose() {
    form.resetFields();
    closeSidePanel();
  }

  function validation(item) {
    return {
      validateStatus: validationErrors()[item] ? "error" : "status",
      help: validationErrors()[item],
    };
  }

  function validationErrors() {
    return mutation.isError
      ? validationErrorParser(mutation.error, ["project_id"], "others")
      : {};
  }

  const handleSelectionChange = (_selectedRowKeys, selectedRows) => {
    setSelectedModels(
      selectedRows.map((selectedRow) => {
        return {
          id: selectedRow.id,
          name: selectedRow.name,
          project_id: selectedRow.project_id,
          project_name: selectedRow.project?.name,
        };
      })
    );
  };

  const FORM_ID = "associateProjectForm";

  function submitComponent() {
    if (associatedInterventions.length === 0) {
      return (
        <Button
          loading={mutation.isPending}
          type="primary"
          htmlType="submit"
          form={FORM_ID}
          disabled={selectedModels.length === 0}
        >
          {selectedModels.length
            ? t("global:associate", { count: selectedModels.length })
            : t("global:associateEmpty")}
        </Button>
      );
    }

    return (
      <Popconfirm
        title={
          <>
            <Typography.Title level={5}>
              {t("project:associate.confirmation.title", {
                project: project.name,
              })}
            </Typography.Title>
            {associatedInterventions
              .slice(0, maxAssociatedDisplay)
              .map((intervention) => (
                <Typography.Paragraph key={intervention.id}>
                  {t("project:associate.confirmation.associatedIntervention", {
                    intervention: intervention.name,
                    project: intervention.project_name,
                  })}
                </Typography.Paragraph>
              ))}
            {associatedInterventions.length > maxAssociatedDisplay ? (
              <Typography.Paragraph>
                {t("project:associate.confirmation.more", {
                  count: associatedInterventions.length - maxAssociatedDisplay,
                })}
              </Typography.Paragraph>
            ) : (
              ""
            )}
          </>
        }
        onConfirm={() => form.submit()}
        okText={t("global:confirm")}
        cancelText={t("global:cancel")}
      >
        <Button
          loading={mutation.isPending}
          type="primary"
          form={FORM_ID}
          disabled={selectedModels.length === 0}
        >
          {selectedModels.length
            ? t("global:associate", { count: selectedModels.length })
            : t("global:associateEmpty")}
        </Button>
      </Popconfirm>
    );
  }
  return (
    <Form id={FORM_ID} form={form} layout="vertical" onFinish={onUpdate}>
      <ListProvider
        endpoint="interventions"
        storageKey={STORAGE_KEYS.PROJECTS_ASSOCIATE}
        includes={[INTERVENTIONS_INCLUDES.PROJECT]}
        hiddenFilters={{
          [INTERVENTIONS_FILTERS.ASSET_LOCATION_ID]: location?.id,
        }}
        lazy={{ scrollY: { vhDiff: TABLE_VH_DIFF } }}
        pageSize={50}
        withContext
      >
        <InterventionsTable
          showLocationColumn={!location}
          showAssetColumn
          hiddenColumns={["users.id"]}
          showExportButton={false}
          showActionsButtons={false}
          rowSelection={{
            selectedRowKeys: selectedModels.map((model) => model.id),
            onChange: handleSelectionChange,
            getCheckboxProps: (intervention) => ({
              disabled: intervention.project_id === project.id,
            }),
          }}
        />
      </ListProvider>
      {validationErrors().others && <Form.Item {...validation("others")} />}
      <Alert
        display={mutation.isError}
        closable={{ afterClose: () => mutation.reset() }}
      />
      <SidePanelFooter>
        <Space>
          <Button onClick={onClose}>{t("global:done")}</Button>
          {submitComponent()}
        </Space>
      </SidePanelFooter>
    </Form>
  );
};

Associate.propTypes = {
  project: PropTypes.object.isRequired,
  model: PropTypes.string.isRequired,
  filters: PropTypes.object,
};

export default Associate;
